import * as React from 'react';
import { SignUp } from 'aws-amplify-react';

import { PasswordRequirements } from './password-requirements';
import { checkPhoneNumber } from './phone-number';
import { withSectionBodyNotes } from './section-body-notes';
import { REFERRAL_SOURCE_KEY, referralSourcePrefill } from './signup-attribution';
import { renderStockScreen } from './stock-screen';

/**
 * Gives the <input> inside one stock FormField a starting value. The stock
 * inputs are uncontrolled and take no value from the config, so this is the
 * only way in; the field stays fully editable, and React ignores a changed
 * defaultValue once the input is mounted. A FormField that does not look as
 * expected is returned untouched (the field just starts blank).
 *
 * Apply it on every render, not only when there is a value: the clone re-keys
 * the FormField's children, and switching between the two shapes would
 * remount the input and drop what was typed.
 */
function withInputDefaultValue(
  formField: React.ReactNode,
  name: string,
  value: string,
): React.ReactNode {
  const element = formField as React.ReactElement<any>;
  if (!element || !element.props) {
    return formField;
  }
  let found = false;
  const children = React.Children.map(element.props.children, (child: any) => {
    if (!found && child && child.props && child.props.name === name) {
      found = true;
      return React.cloneElement(child, { defaultValue: value });
    }
    return child;
  });
  return found ? React.cloneElement(element, element.props, children) : formField;
}

// Carries the same asterisk every label does, so the note explains the marker.
// Both are there because the note alone was easy to skim past (see signUpConfig).
const ALL_REQUIRED_NOTE = (
  <div className="ls-auth-hint ls-auth-intro-note" key="ls-all-required">
    * All fields are required.
  </div>
);

// Notes injected directly beneath the sign-up field of the matching key. The
// keys are the `signUpFields` keys configured in SecureApp.tsx — `username` is
// the Email field, which doubles as the Cognito username.
const FIELD_NOTES: { [key: string]: React.ReactNode } = {
  // The permanence warning earns its place here: nothing in the app can change
  // an address once the account exists (no updateUserAttributes call anywhere,
  // and Settings has no email field), and a practitioner who signs up with a
  // school address loses the account when they graduate.
  //
  // School is named deliberately and business email is not — practitioners
  // routinely use a practice address and that is fine, so warning them off it
  // would be wrong advice.
  username: (
    <div className="ls-auth-hint ls-auth-field-note" key="ls-username-note">
      This becomes the sign-in username. It is <strong>case-sensitive</strong> and{' '}
      <strong>cannot be changed later</strong> — avoid an address that may stop working, such as a
      school email.
    </div>
  ),
  password: <PasswordRequirements className="ls-auth-field-note" key="ls-password-requirements" />,
};

/**
 * The stock Amplify v3 SignUp screen, plus the account guidance practitioners
 * need while they are creating the account: that the email address becomes
 * their case-sensitive username, and what the password policy is.
 */
export class LabSmartsSignUp extends SignUp {
  /**
   * Every field on this form must be filled in. Amplify's own check reads the
   * same `required` flags, but this replaces it so the rule does not depend on
   * how each field is flagged (see signUpConfig).
   *
   * Blank-after-trimming counts as empty, so a single space cannot get past
   * the two free-text questions (Practitioner Type, How Did You Hear About Us).
   */
  validate(): any[] {
    return this.signUpFields
      .filter(field => {
        const value =
          field.key === 'phone_number' ? this.phone_number : this.inputs[field.key];
        return !(typeof value === 'string' ? value.trim() : value);
      })
      .map(field => field.label);
  }

  /**
   * Missing fields are reported first, by the base signUp() through validate().
   * Only a complete form gets the phone check (see checkPhoneNumber), and a
   * number that passes goes to Cognito in its cleaned-up form.
   */
  signUp(): void {
    if (this.validate().length === 0 && this.phone_number) {
      const check = checkPhoneNumber(this.phone_number);
      if (check.error) {
        this.error(check.error);
        return;
      }
      this.phone_number = check.phoneNumber;
    }
    super.signUp();
  }

  showComponent(theme: any): React.ReactNode {
    // Amplify empties `inputs` each time this screen is shown, so a ref
    // pre-fill (see signup-attribution.ts) is re-seeded here. Anything the
    // practitioner has typed since, an empty field included, is left alone.
    if (this.inputs[REFERRAL_SOURCE_KEY] === undefined) {
      const prefill = referralSourcePrefill();
      if (prefill) {
        this.inputs[REFERRAL_SOURCE_KEY] = prefill;
      }
    }
    const referralValue = this.inputs[REFERRAL_SOURCE_KEY] || '';

    return withSectionBodyNotes(
      renderStockScreen(this, SignUp, () => super.showComponent(theme)),
      'sign-up-body-section',
      fields => {
        // The base render emits exactly one element per configured field, in
        // the order `sortFields()` left them.
        if (fields.length !== this.signUpFields.length) {
          return null;
        }

        const annotated: React.ReactNode[] = [ALL_REQUIRED_NOTE];
        this.signUpFields.forEach((field, index) => {
          annotated.push(
            field.key === REFERRAL_SOURCE_KEY
              ? withInputDefaultValue(fields[index], REFERRAL_SOURCE_KEY, referralValue)
              : fields[index],
          );
          const note = FIELD_NOTES[field.key];
          if (note) {
            annotated.push(note);
          }
        });
        return annotated;
      },
    );
  }
}
